import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid-pro';
import { getColumns, displayColumnName } from '../../api/spreadsheet';
import type { Column } from '../../api/spreadsheet';
import { useSpreadsheetViewType } from '../../context/CampaignContext';
import { useSnackbar } from '../../context/SnackbarContext';
import { useUserContextController } from '../../context/UserContext';
import ColumnMappingComponent from '../Prospect/ColumnMappingComponent';
import DataPreviewTable from './CSVUploadModal/DataPreviewTable';
import { CampaignProspectType } from '../../api/campaigns';
import { EnumProspectType } from '../../types/prospects';
import { SpreadsheetColumnTypeMap } from '../../constants/spreadsheetColumnTypes';
import ImportHubspot from '../Prospect/HubSpot/ImportHubspot';
import ImportSalesforce from '../Prospect/Salesforce/ImportSalesforce';
import type { HubSpotImportSourceConfig } from '../../api/campaignScheduledJobs';
import { getHubSpotSchemaForMapping } from '../../api/connector/hubspot';
import { HubSpotTargetObjectName } from '../../types/hubspot';
import HubSpotCompanyPicker from '../Prospect/HubSpot/HubSpotCompanyPicker';

type Step = 'choice' | 'import' | 'mapping';
type CRMSource = 'hubspot' | 'salesforce' | null;

interface CRMImportModalProps {
  open: boolean;
  onClose: () => void;
  prospectType: CampaignProspectType;
  onMappingComplete?: (
    uploadedRows: Record<string, any>[],
    mapping: Record<string, string | null>,
    customColumnTypes?: SpreadsheetColumnTypeMap
  ) => void | Promise<void>;
  /** When set, modal is used to configure a scheduled HubSpot import (skip import step, add scope + primary key). */
  scheduleConfig?: {
    mode: 'hubspot_import';
    initialConfig?: HubSpotImportSourceConfig | null;
    onScheduleConfirm: (config: HubSpotImportSourceConfig) => void;
  };
}

/** Map campaign prospect type to enum used by HubSpot import (Companies vs Contacts). */
function toEnumProspectType(pt: CampaignProspectType): EnumProspectType {
  return pt === CampaignProspectType.ACCOUNT
    ? EnumProspectType.ACCOUNT
    : EnumProspectType.LEAD;
}

function importMappingToInverseMapping(
  import_mapping: HubSpotImportSourceConfig['import_mapping']
): Record<string, string | null> {
  const inv: Record<string, string | null> = {};
  for (const m of import_mapping) {
    inv[m.campaign_column] = m.hubspot_property;
  }
  return inv;
}

function primaryFromImportMappingList(
  import_mapping: HubSpotImportSourceConfig['import_mapping']
): { column: string; hubspotProp: string } {
  const p = import_mapping.find((m) => m.is_primary)!;
  return { column: p.campaign_column, hubspotProp: p.hubspot_property };
}

const CRMImportModal: React.FC<CRMImportModalProps> = ({
  open,
  onClose,
  prospectType,
  onMappingComplete,
  scheduleConfig
}) => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const viewType = useSpreadsheetViewType();
  const { showSnackbar } = useSnackbar();
  const [userContext] = useUserContextController();

  const [step, setStep] = useState<Step>('choice');
  const [crmSource, setCrmSource] = useState<CRMSource>(null);
  const [csvData, setCsvData] = useState<Record<string, any>[]>([]);
  const [detectedColumns, setDetectedColumns] = useState<string[]>([]);
  const [columnsDefinition, setColumnsDefinition] = useState<GridColDef[]>([]);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [importing, setImporting] = useState(false);
  const [scheduleScope, setScheduleScope] =
    useState<HubSpotImportSourceConfig['scope']>('all');
  const [schedulePrimaryColumn, setSchedulePrimaryColumn] = useState('');
  const [schedulePrimaryHubSpotProperty, setSchedulePrimaryHubSpotProperty] =
    useState('');
  const [scheduleMapping, setScheduleMapping] = useState<
    Record<string, string | null>
  >({});
  const [scheduleHubSpotColumnLabels, setScheduleHubSpotColumnLabels] =
    useState<Record<string, string>>({});
  const [scheduleCompanyIds, setScheduleCompanyIds] = useState<string[]>([]);

  const hasSalesforce = !!userContext?.connectedAccounts?.some(
    (a) => a.app === 'salesforce_rest_api' && !!a.connectionId
  );
  const hasHubspot = !!userContext?.connectedAccounts?.some(
    (a) => a.app === 'hubspot' && !!a.connectionId
  );

  // Auto-select CRM from connected accounts - never ask user to pick
  const resolvedCrm: CRMSource =
    hasHubspot && !hasSalesforce
      ? 'hubspot'
      : hasSalesforce
        ? 'salesforce'
        : null;
  const effectiveCrmSource = crmSource ?? resolvedCrm;

  const isScheduleMode =
    !!scheduleConfig && scheduleConfig.mode === 'hubspot_import';
  const effectiveViewType = isScheduleMode ? 'account' : viewType;

  useEffect(() => {
    if (open && resolvedCrm) {
      setCrmSource(resolvedCrm);
      if (isScheduleMode && resolvedCrm === 'hubspot') {
        setStep('mapping');
        if (scheduleConfig?.initialConfig) {
          const cfg = scheduleConfig.initialConfig;
          setScheduleScope(cfg.scope || 'all');
          setScheduleCompanyIds(cfg.company_ids ?? []);
          setScheduleMapping(importMappingToInverseMapping(cfg.import_mapping));
          const primary = primaryFromImportMappingList(cfg.import_mapping);
          setSchedulePrimaryColumn(primary.column);
          setSchedulePrimaryHubSpotProperty(primary.hubspotProp);
        } else {
          setScheduleScope('all');
          setSchedulePrimaryColumn('');
          setSchedulePrimaryHubSpotProperty('');
          setScheduleMapping({});
          setScheduleCompanyIds([]);
        }
      } else {
        setStep('import');
      }
    }
  }, [open, resolvedCrm, isScheduleMode, scheduleConfig?.initialConfig]);

  useEffect(() => {
    if (open && isScheduleMode && effectiveCrmSource === 'hubspot') {
      getHubSpotSchemaForMapping(HubSpotTargetObjectName.COMPANY)
        .then((res) => {
          const fields = res.fields || [];
          setDetectedColumns(fields.map((f) => f.apiName));
          const labels: Record<string, string> = {};
          fields.forEach((f) => {
            labels[f.apiName] = f.label ?? f.apiName;
          });
          setScheduleHubSpotColumnLabels(labels);
        })
        .catch(() => {
          setDetectedColumns([]);
          setScheduleHubSpotColumnLabels({});
        });
    }
  }, [open, isScheduleMode, effectiveCrmSource]);

  // Default primary key to the first mapped pair when in schedule mode
  useEffect(() => {
    if (!isScheduleMode || step !== 'mapping') return;
    const pairs = Object.entries(scheduleMapping).filter(
      (entry): entry is [string, string] => !!entry[1]
    );
    if (pairs.length === 0) {
      setSchedulePrimaryColumn('');
      setSchedulePrimaryHubSpotProperty('');
      return;
    }
    const currentValid =
      schedulePrimaryColumn &&
      schedulePrimaryHubSpotProperty &&
      scheduleMapping[schedulePrimaryColumn] === schedulePrimaryHubSpotProperty;
    if (!currentValid) {
      setSchedulePrimaryColumn(pairs[0][0]);
      setSchedulePrimaryHubSpotProperty(pairs[0][1]);
    }
  }, [isScheduleMode, step, scheduleMapping]);

  useEffect(() => {
    if (open && campaignId) {
      const fetchColumns = async () => {
        setLoadingColumns(true);
        try {
          const response = await getColumns(campaignId, effectiveViewType);
          const columns: Column[] = (response.columns || []).sort(
            (a: Column, b: Column) => (a.col_idx ?? 0) - (b.col_idx ?? 0)
          );
          const gridCols: GridColDef[] = columns.map((col) => ({
            field: col.col_name,
            headerName: col.col_name,
            width: 150,
            sortable: true,
            filterable: false,
            editable: false
          }));
          setColumnsDefinition(gridCols);
        } catch (error: any) {
          showSnackbar(
            error?.message || 'Failed to load campaign columns',
            'error'
          );
        } finally {
          setLoadingColumns(false);
        }
      };
      fetchColumns();
    }
  }, [open, campaignId, effectiveViewType, showSnackbar]);

  const resetState = () => {
    setStep('choice');
    setCrmSource(null);
    setCsvData([]);
    setDetectedColumns([]);
    setScheduleScope('all');
    setSchedulePrimaryColumn('');
    setSchedulePrimaryHubSpotProperty('');
    setScheduleMapping({});
    setScheduleHubSpotColumnLabels({});
    setScheduleCompanyIds([]);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleImportConfirm = (rows: Record<string, any>[]) => {
    setCsvData(rows);
    setDetectedColumns(
      Object.keys(rows[0] || {}).sort((a, b) => a.localeCompare(b))
    );
    setStep('mapping');
  };

  const handleMappingConfirm = async (
    mapping: Record<string, string | null>,
    customColumnTypes?: SpreadsheetColumnTypeMap
  ) => {
    if (scheduleConfig?.mode === 'hubspot_import') {
      if (!schedulePrimaryColumn || !schedulePrimaryHubSpotProperty) {
        showSnackbar('Select a primary key column to continue.', 'warning');
        return;
      }
      if (scheduleScope === 'company_list' && scheduleCompanyIds.length === 0) {
        showSnackbar(
          'Select at least one company for "Company list".',
          'warning'
        );
        return;
      }
      const import_mapping: HubSpotImportSourceConfig['import_mapping'] =
        Object.entries(mapping)
          .filter(
            (entry): entry is [string, string] =>
              entry[1] != null && entry[1] !== ''
          )
          .map(([campaign_column, hubspot_property]) => ({
            hubspot_property,
            campaign_column,
            is_primary:
              campaign_column === schedulePrimaryColumn &&
              hubspot_property === schedulePrimaryHubSpotProperty
          }));
      const config: HubSpotImportSourceConfig = {
        import_mapping,
        scope: scheduleScope
      };
      if (scheduleScope === 'company_list') {
        config.company_ids = scheduleCompanyIds;
      }
      scheduleConfig.onScheduleConfirm(config);
      handleClose();
      return;
    }

    const allFields = Object.keys(mapping);
    const uploadedRows = csvData.map((row) => {
      const newRow: Record<string, any> = {};
      allFields.forEach((field) => {
        const sourceCol = mapping[field];
        newRow[field] = sourceCol ? (row[sourceCol] ?? '') : '';
      });
      return newRow;
    });

    if (uploadedRows.length === 0) {
      showSnackbar('No rows to import', 'warning');
      return;
    }

    if (onMappingComplete) {
      setImporting(true);
      try {
        await onMappingComplete(uploadedRows, mapping, customColumnTypes);
        handleClose();
      } catch (err: any) {
        showSnackbar(
          err?.message || 'Failed to import data into spreadsheet',
          'error'
        );
      } finally {
        setImporting(false);
      }
    } else {
      handleClose();
    }
  };

  const handleBackFromImport = () => {
    handleClose();
  };

  const handleBackFromMapping = () => {
    if (isScheduleMode) {
      handleClose();
    } else {
      setStep('import');
    }
  };

  const title =
    step === 'mapping'
      ? isScheduleMode
        ? 'Schedule import from HubSpot'
        : 'Map columns to spreadsheet'
      : effectiveCrmSource === 'hubspot'
        ? 'import from HubSpot'
        : effectiveCrmSource === 'salesforce'
          ? 'import from Salesforce'
          : 'import from your CRM';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={step === 'mapping' ? 'lg' : 'md'}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <IconButton onClick={handleClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2 }}>
        {(step === 'import' || (step === 'choice' && resolvedCrm)) &&
          effectiveCrmSource === 'hubspot' && (
            <ImportHubspot
              prospectType={toEnumProspectType(prospectType)}
              onConfirm={handleImportConfirm}
              onCancel={handleBackFromImport}
              hideCancel={false}
            />
          )}

        {(step === 'import' || (step === 'choice' && resolvedCrm)) &&
          effectiveCrmSource === 'salesforce' && (
            <ImportSalesforce
              prospectType={toEnumProspectType(prospectType)}
              onConfirm={handleImportConfirm}
              onCancel={handleBackFromImport}
            />
          )}

        {step === 'mapping' && (
          <>
            {!isScheduleMode &&
              csvData.length > 0 &&
              detectedColumns.length > 0 && (
                <DataPreviewTable columns={detectedColumns} rows={csvData} />
              )}
            {isScheduleMode && (
              <Box
                sx={{ my: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="scope-label">Scope</InputLabel>
                  <Select
                    labelId="scope-label"
                    value={scheduleScope}
                    label="Scope"
                    onChange={(e) =>
                      setScheduleScope(
                        e.target.value as HubSpotImportSourceConfig['scope']
                      )
                    }
                  >
                    <MenuItem value="all">All companies</MenuItem>
                    <MenuItem value="my_companies">My companies</MenuItem>
                    <MenuItem value="company_list">Company list</MenuItem>
                  </Select>
                </FormControl>
                {scheduleScope === 'company_list' && (
                  <HubSpotCompanyPicker
                    selectedIds={scheduleCompanyIds}
                    onSelectionChange={setScheduleCompanyIds}
                  />
                )}
                <FormControl fullWidth size="small" sx={{ minWidth: 260 }}>
                  <InputLabel id="primary-key-label">
                    Primary key column
                  </InputLabel>
                  <Select
                    labelId="primary-key-label"
                    value={schedulePrimaryColumn}
                    label="Primary key column"
                    onChange={(e) => {
                      const col = e.target.value;
                      setSchedulePrimaryColumn(col);
                      setSchedulePrimaryHubSpotProperty(
                        col ? scheduleMapping[col] || '' : ''
                      );
                    }}
                  >
                    <MenuItem value="">
                      <em>Select column</em>
                    </MenuItem>
                    {Object.entries(scheduleMapping).map(([col]) => (
                      <MenuItem key={col} value={col}>
                        {displayColumnName(col)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            {loadingColumns ? (
              <Typography color="text.secondary">Loading columns…</Typography>
            ) : (
              <ColumnMappingComponent
                existingColumns={columnsDefinition}
                detectedColumns={detectedColumns}
                protectedFields={[]}
                mappingTitle="Map CRM columns to spreadsheet columns"
                detectedColumnLabels={
                  isScheduleMode ? scheduleHubSpotColumnLabels : undefined
                }
                initialMapping={
                  scheduleConfig?.initialConfig
                    ? importMappingToInverseMapping(
                        scheduleConfig.initialConfig.import_mapping
                      )
                    : undefined
                }
                onConfirm={handleMappingConfirm}
                onCancel={handleBackFromMapping}
                onMappingChange={
                  isScheduleMode ? setScheduleMapping : undefined
                }
                triggeredFromProspectPage={false}
                hideButtons={false}
                primaryDisabled={
                  isScheduleMode &&
                  (!schedulePrimaryColumn ||
                    !schedulePrimaryHubSpotProperty ||
                    (scheduleScope === 'company_list' &&
                      scheduleCompanyIds.length === 0))
                }
                primaryDisabledTooltip={
                  isScheduleMode &&
                  scheduleScope === 'company_list' &&
                  scheduleCompanyIds.length === 0
                    ? 'Select at least one company to continue.'
                    : 'Select a primary key column to continue.'
                }
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CRMImportModal;
